/**
 * Approve price on a purchase order held for price approval (#94): the
 * supplier confirmed a price past its limit, and a manager or admin accepts it
 * as what we pay, with why. The customer's price does not change. The view's
 * `approve_price_url` says whether the caller may; the server's refusal, bound
 * to the field it names, replaces the check made as typed. `onApprove`
 * resolves to the refusal to show, or null once done.
 */
import { useId, useState } from "react";
import type { ApprovePurchaseOrderPriceRequest } from "@shared-core/data/order-to-invoice-types";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import type { OrderStepFailure } from "@shared-core/domain/order-to-invoice-presentation";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Textarea } from "../../primitives/textarea";
import { FailureBanner, Field } from "./OrderDialogs";

const MAX_NOTE_LENGTH = 2000;

export function ApprovePriceDialog({
  purchaseOrderNo,
  supplierName,
  costPence,
  busy,
  onClose,
  onApprove,
}: {
  purchaseOrderNo: string;
  supplierName: string;
  /** What the purchase order costs at the supplier's confirmed price, ex VAT. */
  costPence: number;
  busy: boolean;
  onClose: () => void;
  onApprove: (
    input: ApprovePurchaseOrderPriceRequest,
  ) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  const [note, setNote] = useState("");
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const problem = !note.trim()
    ? "Enter the reason."
    : note.trim().length > MAX_NOTE_LENGTH
      ? `The note can be at most ${MAX_NOTE_LENGTH} characters.`
      : undefined;
  const noteError =
    failure?.fieldErrors.note ?? (failure ? problem : undefined);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Approve price</DialogTitle>
          <DialogDescription>
            {supplierName}&apos;s price on {purchaseOrderNo} moved past its
            limit. Approving accepts {formatQuotedPrice(costPence)} ex VAT as
            what we pay and places the order. The customer&apos;s price does not
            change.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            if (problem) {
              setFailure({
                summary: "One field needs correcting.",
                fieldErrors: {},
              });
              return;
            }
            const refused = await onApprove({ note: note.trim() });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <Field id={`${id}-note`} label="Why" error={noteError}>
            {(aria) => (
              <Textarea
                {...aria}
                rows={2}
                value={note}
                placeholder="They raised paper prices; agreed with Sam"
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Back
            </Button>
            <Button type="submit" disabled={busy}>
              Approve price
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
